import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { api } from '../../api/client';
import type { ApiClientSummary, OwnerSummary } from '@pay/shared';

export default function Clients() {
  const [clients, setClients] = useState<ApiClientSummary[]>([]);
  const [owners, setOwners] = useState<OwnerSummary[]>([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', owner_ids: [] as string[], webhook_secret: '' });

  function refresh() {
    api.get<ApiClientSummary[]>('/api/admin/clients').then(setClients).catch(() => {});
    api.get<OwnerSummary[]>('/api/admin/owners').then(setOwners).catch(() => {});
  }
  useEffect(refresh, []);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    await api.post('/api/admin/clients', form);
    setCreating(false);
    setForm({ name: '', owner_ids: [], webhook_secret: '' });
    refresh();
  }

  return (
    <div className="p-6 max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold">API-klienter</h1>
        <button
          onClick={() => setCreating((v) => !v)}
          className="flex items-center gap-2 bg-brand hover:bg-brand-dark text-white rounded px-3 py-2 text-sm font-medium"
        >
          <Plus size={16} /> Ny klient
        </button>
      </div>

      {creating && (
        <form onSubmit={create} className="bg-white rounded-lg shadow border border-slate-200 p-5 mb-5 space-y-4">
          <Field label="Navn (f.eks. 'ElseWeb-tenant Demoservice')">
            <input className="input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required />
          </Field>
          <Field label="Webhook-hemmelighet (brukes for X-Pay-Signature)">
            <input className="input" value={form.webhook_secret} onChange={(e) => setForm((f) => ({ ...f, webhook_secret: e.target.value }))} placeholder="Valgfri — settes hvis kallesystemet skal verifisere signatur" />
          </Field>
          <Field label="Eiere klienten kan opprette krav for">
            <div className="space-y-1">
              {owners.map((o) => (
                <label key={o.id} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.owner_ids.includes(o.id)}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        owner_ids: e.target.checked ? [...f.owner_ids, o.id] : f.owner_ids.filter((x) => x !== o.id),
                      }))
                    }
                  />
                  {o.name}
                </label>
              ))}
            </div>
          </Field>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setCreating(false)} className="px-3 py-2 text-sm">
              Avbryt
            </button>
            <button type="submit" className="bg-brand hover:bg-brand-dark text-white rounded px-3 py-2 text-sm font-medium">
              Opprett
            </button>
          </div>
        </form>
      )}

      <div className="bg-white rounded-lg shadow border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
            <tr>
              <th className="text-left px-6 py-3">Navn</th>
              <th className="text-left px-6 py-3">Eiere</th>
              <th className="text-left px-6 py-3">Opprettet</th>
            </tr>
          </thead>
          <tbody>
            {clients.map((c) => (
              <tr key={c.id} className="border-t border-slate-100">
                <td className="px-6 py-3">
                  <Link to={`/admin/clients/${c.id}`} className="text-brand hover:underline font-medium">
                    {c.name}
                  </Link>
                </td>
                <td className="px-6 py-3 text-slate-600">{c.scopes.map((s) => s.owner_name).join(', ')}</td>
                <td className="px-6 py-3 text-slate-600">{new Date(c.created_at).toLocaleDateString('nb-NO')}</td>
              </tr>
            ))}
            {clients.length === 0 && (
              <tr>
                <td colSpan={3} className="px-6 py-8 text-center text-slate-500">
                  Ingen klienter ennå.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <style>{`.input { width:100%; border:1px solid #cbd5e1; border-radius:4px; padding:6px 10px; }`}</style>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 mb-1">{label}</span>
      {children}
    </label>
  );
}
